import React, { useState } from 'react';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Box,
  Button,
  CssBaseline,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';
import { images } from '../../shared/images/images';
import logoNuevo from '../../shared/images/logoEmcali.jpg';
import { getProyectosApiBaseUrl } from '../../utils/proyectosApiBaseUrl';

const schema = Yup.object({
  correo: Yup.string().email('Correo invalido').required('El correo es obligatorio'),
});

const theme = createTheme({
  palette: { primary: { main: '#0a2f57' } },
  shape: { borderRadius: 14 },
});

export default function OlvidoContrasenaPage() {
  const [ok, setOk] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: { correo: string }, { setSubmitting }: { setSubmitting: (v: boolean) => void }) => {
    try {
      setError(null);
      setOk(null);
      const res = await fetch(`${getProyectosApiBaseUrl()}/auth/olvido-clave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correo: values.correo.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'No fue posible procesar la solicitud.');
      setOk(data.mensaje || 'Revise su bandeja de correo.');
    } catch (e: any) {
      setError(e.message || 'Error al enviar la solicitud.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid
        container
        component="main"
        sx={{
          minHeight: '100vh',
          backgroundImage: `linear-gradient(rgba(10,47,87,.50), rgba(10,47,87,.70)), url(${images.SliderLogin})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <CssBaseline />
        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Paper elevation={8} sx={{ width: '100%', maxWidth: 460, p: 4, borderRadius: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box component="img" sx={{ width: 160, objectFit: 'contain' }} alt="Logo Emcali" src={logoNuevo} />
              <Typography variant="h5" fontWeight={700} color="primary.main">
                Recuperar contraseña
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Indique el correo registrado en su cuenta. Le enviaremos un enlace para restablecer la contraseña.
              </Typography>
            </Box>

            {ok && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {ok}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Formik initialValues={{ correo: '' }} validationSchema={schema} onSubmit={handleSubmit}>
              {({ isSubmitting, touched, errors }) => (
                <Form>
                  <Field
                    as={TextField}
                    name="correo"
                    label="Correo electronico"
                    type="email"
                    fullWidth
                    margin="normal"
                    autoFocus
                    InputProps={{
                      startAdornment: <EmailOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />,
                    }}
                    error={Boolean(touched.correo && errors.correo)}
                    helperText={<ErrorMessage name="correo" />}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{ mt: 2, py: 1.2, fontWeight: 700, textTransform: 'none' }}
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar enlace de recuperación'}
                  </Button>
                </Form>
              )}
            </Formik>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link component={RouterLink} to="/" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <ArrowBackIcon fontSize="small" />
                Volver al inicio de sesión
              </Link>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}
